import React from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { CLASSIFICATIONS, DEFAULT_CLASS, CLASS_BY_KEY } from "../lib/classification";

/**
 * ClassSelect — dropdown for `business_classification` that renders a
 * coloured swatch next to every option (and next to the current value on
 * the trigger). Uses the shadcn Radix Select so we can put custom JSX
 * inside each item — native <option> can't render inline elements.
 *
 * Props are shaped like a controlled form field so it drops into the
 * existing New / Edit project forms without changing the parent state:
 *   value    – current class key (e.g. "retro")
 *   onChange – called with the new class key
 *   required – shows the standard danger asterisk styling
 *   testId   – the trigger element's data-testid (so existing tests still hit
 *              e.g. `np-classification`)
 */
function Swatch({ tone, size = 10 }) {
  return (
    <span
      aria-hidden
      className="inline-block rounded-full flex-shrink-0"
      style={{
        width: size,
        height: size,
        background: tone,
        boxShadow: `0 0 0 1px ${tone}66`,
      }}
    />
  );
}

export default function ClassSelect({ value, onChange, testId }) {
  const selected = CLASS_BY_KEY[value] || CLASS_BY_KEY[DEFAULT_CLASS];
  return (
    <Select value={value || DEFAULT_CLASS} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testId}
        className="input-swiss h-auto py-2 text-sm"
      >
        <SelectValue>
          <span className="inline-flex items-center gap-2">
            <Swatch tone={selected.tone} />
            <span className="font-medium">{selected.label}</span>
            <span className="muted text-xs">— {selected.sub}</span>
          </span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {CLASSIFICATIONS.map((c) => (
          <SelectItem
            key={c.key}
            value={c.key}
            data-testid={`${testId}-option-${c.key}`}
          >
            <span className="inline-flex items-center gap-2">
              <Swatch tone={c.tone} />
              <span className="font-medium" style={{ color: c.tone }}>
                {c.label}
              </span>
              <span className="muted text-xs">— {c.sub}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
